import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FOUNDERS, type FounderEntry } from "@/lib/brand";
import { useLocalized } from "@/hooks/useLocalized";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTimelineRowMotion } from "@/hooks/useTimelineRowMotion";

const FounderCard = ({ founder }: { founder: FounderEntry }) => {
    const { t } = useTranslation();
    const { lang } = useLanguage();
    const background = useLocalized({ ar: founder.backgroundAr, en: founder.backgroundEn });
    const quote = useLocalized({ ar: founder.quoteAr, en: founder.quoteEn });
    const name = lang === "en" ? founder.nameEn : founder.nameAr;
    const title = lang === "en" ? founder.titleEn : founder.titleAr;
    const secondaryName = lang === "en" ? founder.nameAr : founder.nameEn;
    const secondaryTitle = lang === "en" ? founder.titleAr : founder.titleEn;
    return (
        <article
            className="glass-v2 hover-lift-v2 rounded-[32px] p-8 md:p-10 group relative overflow-hidden h-full flex flex-col"
            data-cursor="hover"
        >
            {/* Ambient hover glow */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(500px circle at 50% 0%, hsl(260 90% 65% / 0.14), transparent 65%)",
                }}
            />

            {/* Decorative opening-quote glyph */}
            <span
                aria-hidden
                className="absolute top-4 right-6 md:right-8 font-v2-display leading-none select-none pointer-events-none text-[140px] md:text-[180px] text-white/[0.04]"
            >
                «
            </span>

            <div className="relative flex-1 flex flex-col">
                {/* Portrait + name header */}
                <header className="flex items-center gap-5 mb-7">
                    <div
                        className="relative shrink-0 w-[100px] h-[100px] md:w-[120px] md:h-[120px] rounded-[24px] overflow-hidden"
                        style={{
                            border: "1.5px solid hsl(260 90% 65% / 0.3)",
                            boxShadow:
                                "0 20px 50px -15px rgba(0,0,0,0.6), 0 0 40px hsl(260 90% 65% / 0.15)",
                        }}
                    >
                        <img
                            src={founder.photo}
                            alt={name}
                            loading="lazy"
                            className="w-full h-full object-cover"
                        />
                        {/* Soft inner vignette */}
                        <div
                            aria-hidden
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background:
                                    "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.25))",
                            }}
                        />
                    </div>

                    <div className="flex-1 min-w-0">
                        <p className="font-v2-label text-[10px] text-primary/80 mb-2">
                            {t("founders_home.role_label")} · {background}
                        </p>
                        <h3 className="font-v2-display text-xl md:text-2xl text-white leading-tight mb-1.5">
                            {name}
                        </h3>
                        <p className="font-v2-display text-xs md:text-sm text-white/60">
                            {secondaryName} · {secondaryTitle}
                        </p>
                    </div>
                </header>

                {/* Divider */}
                <div
                    aria-hidden
                    className="h-px w-full opacity-30 mb-6"
                    style={{
                        background:
                            "linear-gradient(90deg, transparent, hsl(260 90% 65% / 0.8), hsl(190 90% 50% / 0.8), transparent)",
                    }}
                />

                {/* Quote */}
                <blockquote className="relative flex-1">
                    <p className="text-base md:text-lg text-white/85 leading-[1.9] font-medium">
                        {quote}
                    </p>
                </blockquote>

                {/* Signature footer */}
                <footer className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between gap-3">
                    <span className="font-v2-label text-[10px] text-white/40">
                        — {name}
                    </span>
                    <span className="font-v2-label text-[10px] text-white/30">
                        {title}
                    </span>
                </footer>
            </div>
        </article>
    );
};

const FounderRow = ({
    founder,
    index,
}: {
    founder: FounderEntry;
    index: number;
}) => {
    const isEven = index % 2 === 0; // 0 → right, 1 → left
    const rowRef = useRef<HTMLLIElement>(null);
    const { y, x, scale } = useTimelineRowMotion(rowRef, isEven);

    return (
        <motion.li
            ref={rowRef}
            style={{ y, x, scale }}
            className="relative md:grid md:grid-cols-2 md:gap-12 lg:gap-16 items-stretch"
        >
            {/* Node — distinct: rounded-square with monogram letter */}
            <div
                aria-hidden
                className="hidden md:flex absolute right-1/2 top-10 translate-x-1/2 w-16 h-16 rounded-[20px] items-center justify-center z-10"
                style={{
                    background: "var(--gradient-accent-v2)",
                    boxShadow:
                        "0 0 36px hsl(260 90% 65% / 0.55), 0 0 80px hsl(190 90% 50% / 0.2)",
                }}
            >
                <span className="font-v2-display text-2xl text-white leading-none">
                    {founder.monogram}
                </span>
            </div>

            <div className={`${isEven ? "md:col-start-1" : "md:col-start-2"}`}>
                <FounderCard founder={founder} />
            </div>
        </motion.li>
    );
};

const FounderSection = () => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"],
    });
    const spineScale = useTransform(scrollYProgress, [0.1, 0.9], [0, 1]);

    return (
        <section
            ref={containerRef}
            className="py-20 md:py-32 relative"
            id="founder"
        >
            <div className="container mx-auto px-6 relative">
                {/* Header */}
                <div className="max-w-3xl mx-auto text-center mb-20">
                    <p className="font-v2-label text-xs text-primary/80 mb-4">
                        {t("founders_home.eyebrow")}
                    </p>
                    <h2 className="font-v2-display text-4xl md:text-6xl text-white mb-6">
                        {t("founders_home.title_prefix")}{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            {t("founders_home.title_highlight")}
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        {t("founders_home.subtitle")}
                    </p>
                </div>

                {/* Timeline */}
                <div className="relative max-w-6xl mx-auto">
                    {/* Base spine */}
                    <div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px bg-white/[0.06]"
                    />
                    {/* Progressive fill */}
                    <motion.div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px origin-top"
                        style={{
                            scaleY: spineScale,
                            background:
                                "linear-gradient(180deg, hsl(260 90% 65% / 0.9), hsl(190 90% 50% / 0.9), hsl(260 90% 65% / 0.4))",
                        }}
                    />

                    <ol className="space-y-14 md:space-y-16">
                        {FOUNDERS.map((f, i) => (
                            <FounderRow key={f.key} founder={f} index={i} />
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    );
};

export default FounderSection;
